import { createSelector } from 'reselect';

import type { Advice } from 'types/Advice';

import { isAdviceAccepted, isAdvicePending } from 'works/utils';
import { ADVICE_STATUS, CHECKLIST_TYPE, SEVERITY } from 'modules/entity/constants';

import { getAdvicesByIds, getAdviceIdsByType } from '.';
import { getRoot } from '../general';

const getPendingAdvices = (advices: Array<Advice>) => advices.filter(isAdvicePending);

export const getVehicleAdvicesIds = createSelector(getAdviceIdsByType, ids => ids[CHECKLIST_TYPE.VEHICLE] ?? []);

const getVehicleAdvices = createSelector(getRoot, getVehicleAdvicesIds, getAdvicesByIds);

const getAccessoryAdvicesIds = createSelector(getAdviceIdsByType, ids => ids[CHECKLIST_TYPE.ACCESSORY] ?? []);

const getAccessoryAdvices = createSelector(getRoot, getAccessoryAdvicesIds, getAdvicesByIds);

export const getTradeInAdvicesIds = createSelector(getAdviceIdsByType, ids => ids[CHECKLIST_TYPE.TRADE_IN] ?? []);

const getTradeInAdvices = createSelector(getRoot, getTradeInAdvicesIds, getAdvicesByIds);

export const getDiscountAdvicesIds = createSelector(getAdviceIdsByType, ids => ids[CHECKLIST_TYPE.DISCOUNT] ?? []);

const getDiscountAdvices = createSelector(getRoot, getDiscountAdvicesIds, getAdvicesByIds);

const getProposedAdvices = (advices: Array<Advice>) => advices.filter(({ severity }) => severity === SEVERITY.PROPOSED);
const getProposedVehicleAdvices = createSelector(getVehicleAdvices, getProposedAdvices);

const hasProposedVehicleAdvice = createSelector(getProposedVehicleAdvices, advices => advices.length > 0);

const getMandatoryAdvicesIds = createSelector(
  hasProposedVehicleAdvice,
  getVehicleAdvicesIds,
  getAccessoryAdvicesIds,
  (hasVehicle, vehicleAdvices, accessoryAdvices) => (hasVehicle ? vehicleAdvices : accessoryAdvices)
);

export const getMandatoryAdvices = createSelector(getRoot, getMandatoryAdvicesIds, getAdvicesByIds);

export const hasProposedMandatoryAdvices = createSelector(getMandatoryAdvices, advices =>
  advices.some(({ severity }) => severity === SEVERITY.PROPOSED));

const getVehicleAndAccessoryAdvicesIds = createSelector(
  getVehicleAdvicesIds,
  getAccessoryAdvicesIds,
  (vehicleAdvices, accessoryAdvices) => [...vehicleAdvices, ...accessoryAdvices]
);

const getVehicleAndAccessoryAdvices = createSelector(getRoot, getVehicleAndAccessoryAdvicesIds, getAdvicesByIds);

export const hasAcceptedMandatoryAdvices = createSelector(getMandatoryAdvices, advices =>
  advices.some(isAdviceAccepted));

const getDisplayedAccessoryAdvices = createSelector(getAccessoryAdvices, advices =>
  advices
    .filter(({ severity }) => severity === SEVERITY.PROPOSED || severity === SEVERITY.OFFERED)
    .sort(advice => (isAdvicePending(advice) ? 1 : -1)));

export const getDisplayedAccessoryAdvicesIds = createSelector(getDisplayedAccessoryAdvices, advices =>
  advices.map(({ adviceId }) => adviceId));

export const hasOffer = createSelector(
  getVehicleAdvicesIds,
  getDisplayedAccessoryAdvices,
  (vehicleAdvices, accessoryAdvices) => vehicleAdvices.length > 0 || accessoryAdvices.length > 0
);

const getAccessoryProposedPendingAdvices = createSelector(getDisplayedAccessoryAdvices, getPendingAdvices);

export const getAccessoryProposedPendingAdvicesIds = createSelector(getAccessoryProposedPendingAdvices, advices =>
  advices.map(({ adviceId }) => adviceId));

const hasAnswered = createSelector(getVehicleAndAccessoryAdvices, advices =>
  advices.some(({ status: adviceStatus }) => adviceStatus !== ADVICE_STATUS.PENDING));

const hasNewAdvices = (advices: Array<Advice>, isAnswered: boolean): boolean => isAnswered && advices.some(isAdvicePending);
const hasNewAdvicesVehicles = createSelector(getVehicleAdvices, hasAnswered, hasNewAdvices);
const hasNewAdvicesAccessory = createSelector(getAccessoryAdvices, hasAnswered, hasNewAdvices);
const hasNewAdvicesDiscount = createSelector(getDiscountAdvices, hasAnswered, hasNewAdvices);
const hasNewAdvicesTradeIn = createSelector(getTradeInAdvices, hasAnswered, hasNewAdvices);

const ANCHORS_CONFIG: Record<string, string> = {
  vehicle: 'works-vehicle',
  accessory: 'works-accessory',
  discount: 'works-discount',
  tradein: 'works-tradein',
};

export const getNewAdvicesAnchor = createSelector(
  hasNewAdvicesVehicles,
  hasNewAdvicesAccessory,
  hasNewAdvicesDiscount,
  hasNewAdvicesTradeIn,
  (vehicle, accessory, discount, tradein) => {
    const hasNewAdvicesByType: Record<string, boolean> = { vehicle, accessory, discount, tradein };
    return Object.entries(ANCHORS_CONFIG).find(([key]) => hasNewAdvicesByType[key])?.[1];
  }
);
